import {
  InstallmentFieldError,
  validateInstallments,
  validateInstallmentsTotal,
} from 'components/common/balance-adjustment/installmentHelpers';
import {
  APIAnswer,
  EMPTY_SELECT_VALUE,
  FormAnswer,
  FormQuestion,
  Question,
  QuestionType,
  REGISTRATION_OPTION_LABEL,
  SelectOption,
} from 'components/registration/v2/PostRegEditDrawer';
import { convertFromPennies, convertToPennies } from 'core/utils';
import {
  Confirmation,
  FormError,
  PendingEdit,
  PostRegDrawerState,
  PostRegEditFormState,
  PostRegFormStructure,
} from './types';

export function getFormQuestions(questions: Question[]): FormQuestion[] {
  return questions.map((question) => {
    const { defaults, type, label, options, multiselect, formFieldId } =
      question;
    let defaultValue;
    // Handle the case where it's a select question with no defaults
    if (
      type === QuestionType.SELECT &&
      !multiselect &&
      label !== REGISTRATION_OPTION_LABEL
    ) {
      options.unshift({ value: EMPTY_SELECT_VALUE, label: EMPTY_SELECT_VALUE });
    }
    if (
      type === QuestionType.SELECT &&
      !multiselect &&
      (defaults.length === 0 || !defaults[0].value)
    ) {
      defaultValue = [{ value: EMPTY_SELECT_VALUE }];
    } else {
      defaultValue =
        type === QuestionType.DATE
          ? defaults.map((val) => ({
              value: val.value
                ? new Date(val.value).toISOString().split('T')[0]
                : '',
            }))
          : defaults;
    }
    if (type === QuestionType.SELECT) {
      return {
        id: formFieldId.toString(),
        type: multiselect ? QuestionType.MULTISELECT : QuestionType.SELECT,
        label,
        defaults: multiselect ? defaults : defaultValue,
        options,
        validation: true,
      };
    }

    return {
      id: formFieldId.toString(),
      type,
      label,
      defaults: defaultValue,
      options,
      validation: true,
    };
  });
}

/**
 * Validate each question in the form:
 * Check if selected values are within the specified amount range:
 *    a. For multiselect questions, validate each selected value.
 *    b. For select questions, validate the selected value.
 * Note: Quantity validation is handled by the frontend dropdown limits.
 * This function mutates updatedErrors.
 */

export function validateQuestion(
  question: FormQuestion,
  updatedErrors: { [key: string]: FormError[] },
  form: PostRegEditFormState
): boolean {
  const { id } = question;
  updatedErrors[id] = [];
  const amountRangeError = (min: number, max: number) =>
    `Amount must be between $${convertFromPennies(
      min
    )} and $${convertFromPennies(max)}`;

  const quantityError = (max: number) =>
    `Quantity must be between 1 and ${max}`;

  for (const option of question.options as SelectOption[]) {
    const formAnswer = form[id].find((answer) => answer.value === option.value);
    if (!formAnswer) {
      continue;
    }
    const { value, details } = formAnswer;
    if (option.amountRange) {
      const [min, max] = option.amountRange;
      const selectedAmountRange =
        details !== undefined ? Number(convertToPennies(String(details))) : 0;
      if (selectedAmountRange < min || selectedAmountRange > max) {
        updatedErrors[id].push({
          value,
          details,
          error: amountRangeError(min, max),
        });
      }
    } else if (option.maxQuantity) {
      const max = option.maxQuantity;

      const selectedQuantity = Number(formAnswer.details);
      if (selectedQuantity < 1 || selectedQuantity > max) {
        updatedErrors[id].push({ value, details, error: quantityError(max) });
      }
    }
  }
  return updatedErrors[id].length === 0;
}

export function validateForm(
  formState: PostRegEditFormState,
  formStructure: PostRegFormStructure,
  updatedErrors: { [key: string]: FormError[] }
): boolean {
  const isFeeRelatedQuestionsValid = formStructure.feeRelatedQuestions.reduce(
    (acc, question) =>
      validateQuestion(question, updatedErrors, formState) && acc,
    true
  );
  // Validate additional questions
  const isAdditionalQuestionsValid = formStructure.additionalQuestions.reduce(
    (acc, question) =>
      validateQuestion(question, updatedErrors, formState) && acc,
    true
  );
  return isFeeRelatedQuestionsValid && isAdditionalQuestionsValid;
}

/**
 *  Get a question by its questionId from the form structure object
 */
export function getQuestion(
  formStructure: PostRegFormStructure,
  id: number | string
): FormQuestion {
  const question = formStructure.feeRelatedQuestions.find((q) => q.id === id);
  if (question) {
    return question;
  }
  return formStructure.additionalQuestions.find((q) => q.id === id);
}

export function areAnswersEqual(
  list1: FormAnswer[],
  list2: FormAnswer[]
): boolean {
  if (
    list1.length === 0 &&
    list2.length === 1 && 
    list2[0].value.trim() === ''
  ) {
    // only entered whitespace
    return true;
  }
  
  if (list1.length !== list2.length) {
    return false;
  }

  const sortedList1 = [...list1].sort((a, b) => a.value.localeCompare(b.value));
  const sortedList2 = [...list2].sort((a, b) => a.value.localeCompare(b.value));

  return JSON.stringify(sortedList1) === JSON.stringify(sortedList2);
}

export function getQuestionDefaults(question: FormQuestion): FormAnswer[] {
  return question.defaults.map((defaultValue) => {
    const option = question.options.find(
      (option) => option.value === defaultValue.value
    ) as SelectOption;

    if (option && option.amountRange) {
      return {
        value: defaultValue.value,
        details: String(
          convertFromPennies(defaultValue.amount)
        ),
      };
    } else if (option && option.maxQuantity) {
      return {
        value: defaultValue.value,
        details: String(
          defaultValue.quantity
        ),
      };
    }
    return { value: defaultValue.value };
  });
}

export const formatValue = (
  question: FormQuestion,
  value: { value: string; details?: string }
) => {
  const option = question.options.find(
    (option) => option.value === value.value
  );
  const label = option?.label || value.value || '';
  let details = value.details;
  if (option && 'amountRange' in option && option['amountRange']) {
    details = '$' + value.details;
  }
  return `${label}${value.details ? ` (${details})` : ''}`;
};

export const formatDateValue = (
  question: FormQuestion,
  value: { value: string; details?: string }
) => {
  // Format into the format mm/dd/yyyy
  if (!value.value) {
    return EMPTY_SELECT_VALUE;
  }
  const [year, month, day] = value.value.split('-').map(Number);
  const formattedDate = `${month}/${day}/${year}`;
  return formatValue(question, {
    value: formattedDate,
    details: value.details,
  });
};

export function formatDetailsToPennies(
  answers: FormAnswer[],
  question: FormQuestion
): APIAnswer[] {
  const { options } = question;
  // convert the details from formatted string to number as pennies if the option has amountRange
  const updatedAnswers: APIAnswer[] = answers.reduce(
    (acc, answer: FormAnswer) => {
      const correspondingOption = options.find((o) => o.value == answer.value);
      if (
        answer.details &&
        correspondingOption &&
        (correspondingOption['amountRange'] ||
          correspondingOption['maxQuantity'])
      ) {
        return [
          ...acc,
          {
            value: answer.value,
            details: Number(
              correspondingOption['amountRange']
                ? convertToPennies(answer.details)
                : answer.details
            ),
          } as APIAnswer,
        ];
      }
      return [...acc, { value: answer.value }];
    },
    [] as APIAnswer[]
  );
  return updatedAnswers;
}

export function formatPendingEdits(state: PostRegDrawerState): PendingEdit[] {
  // convert the dirty fields to pending_edits format for api
  const { dirtyFields, formStructure, currentFormState } = state;
  const pendingEdits = dirtyFields.map((questionId) => {
    const question = getQuestion(formStructure, questionId);
    let answersArray = currentFormState[questionId]; // array of strings
    if (question.type === QuestionType.SELECT) {
      // filter blank values out of multiselect
      answersArray = [...answersArray].filter(
        (a) => a.value !== '' && a.value !== EMPTY_SELECT_VALUE
      );
    } else if (question.type === QuestionType.MULTISELECT) {
      // filter blank values out of multiselect
      answersArray = [...answersArray].filter((a) => a.value !== '');
    }
    return {
      questionId,
      answers: formatDetailsToPennies(answersArray, question),
    };
  });
  return pendingEdits;
}

export function getInstallmentErrors({
  pendingPayment,
  previewLineItems,
}: Partial<PostRegDrawerState>): {
  hasError: boolean;
  error?: string;
  fieldErrors?: Record<string, InstallmentFieldError>;
} {
  const newBalance = previewLineItems?.newBalance;
  const installments = pendingPayment?.installments || [];
  if (newBalance < 0 || !installments || installments.length === 0) {
    // dont validate if there is no balance or no installments
    return { hasError: false, error: undefined, fieldErrors: {} };
  }
  const fieldErrors = validateInstallments(installments, 100, newBalance);
  const error = validateInstallmentsTotal(newBalance, installments);
  const hasError = Boolean(Object.keys(fieldErrors).length > 0 || error);
  return { hasError, error, fieldErrors };
}

export function getLocalUpdatedFlags(
  state: PostRegDrawerState,
): Partial<Confirmation> & { feeRelatedQuestionUpdated: boolean } {
  const additionalQuestionsUpdated = state.dirtyFields.some((questionId) =>
    state.formStructure.additionalQuestions.some(
      (question) => question.id === questionId
    )
  );
  const participantGroupUpdated = state
    .dirtyFields.some((questionId) =>
      state.formStructure.feeRelatedQuestions.some(
        (question) => question.id === questionId && question.label === REGISTRATION_OPTION_LABEL
      )
    );
  const feeRelatedQuestionUpdated = state
    .dirtyFields.some((questionId) =>
      state.formStructure.feeRelatedQuestions.some(
        (question) => question.id === questionId
      )
    );
  return {
    additionalQuestionsUpdated,
    participantGroupUpdated,
    feeRelatedQuestionUpdated,
  };
}
